/* eslint-disable react/prop-types */
// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";

import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";

// Images
import team2 from "assets/images/team-2.jpg";
import team3 from "assets/images/team-3.jpg";
import team4 from "assets/images/team-4.jpg";
import { Button } from "@mui/material";
import { Link } from "react-router-dom";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import ChatIcon from "@mui/icons-material/Chat";
import { useNavigate } from "react-router-dom";

// Raised By component with navigation
const RaisedBy = ({ name, email, userType, userId }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (userType === "PARENT" && userId) {
      navigate(`/parents/profile/${userId}`);
    }
  };

  return (
    <ArgonBox display="flex" flexDirection="column">
      <ArgonTypography
        variant="caption"
        color={userType === "PARENT" ? "primary" : "text"}
        fontWeight="medium"
        onClick={handleClick}
        sx={{
          cursor: userType === "PARENT" ? "pointer" : "default",
          textDecoration: userType === "PARENT" ? "underline" : "none",
          "&:hover": {
            color: userType === "PARENT" ? "#1976d2" : "text",
          },
        }}
      >
        {name}
      </ArgonTypography>
      <ArgonTypography variant="caption" color="secondary">
        {email}
      </ArgonTypography>
    </ArgonBox>
  );
};

function Author({ image, name, email }) {
  return (
    <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
      <ArgonBox mr={2}>
        <ArgonAvatar src={image} alt={name} size="sm" variant="full" />
      </ArgonBox>
      <ArgonBox display="flex" flexDirection="column">
        <ArgonTypography variant="button" fontWeight="medium">
          {name}
        </ArgonTypography>
        <ArgonTypography variant="caption" color="secondary">
          {email}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );
}

function Function({ job, org }) {
  return (
    <ArgonBox display="flex" flexDirection="column">
      <ArgonTypography variant="caption" fontWeight="medium" color="text">
        {job}
      </ArgonTypography>
      <ArgonTypography variant="caption" color="secondary">
        {org}
      </ArgonTypography>
    </ArgonBox>
  );
}

function StudentData({ student, client }) {
  return (
    <ArgonBox display="flex" flexDirection="column">
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        {student}
      </ArgonTypography>
      <ArgonTypography variant="caption" color="text" fontWeight="medium">
        Client-{client}
      </ArgonTypography>
    </ArgonBox>
  );
}

// Attendance component
function AttendanceProgress({ percentage, label = "Attendance" }) {
  return (
    <ArgonBox>
      {/* Label */}
      <ArgonBox display="flex" justifyContent="space-between" mb={1}>
        <ArgonTypography variant="caption" fontWeight="medium" color="text">
          {"12 July 2024"}
        </ArgonTypography>
      </ArgonBox>

      {/* Progress Bar Container */}
      <ArgonBox
        width="180%"
        height="10px"
        bgColor="grey-200"
        borderRadius="lg"
        position="relative"
        overflow="hidden"
      >
        {/* Progress Fill */}
        <ArgonBox
          width={`${percentage}%`}
          height="100%"
          // bgColor={percentage >= 80 ? "success" : percentage >= 60 ? "warning" : "error"}
          bgColor="primary"
          borderRadius="lg"
          position="absolute"
          top={0}
          left={0}
        />
      </ArgonBox>
    </ArgonBox>
  );
}

const supportTicketTableData = (
  handleViewClick,
  handleStatusClick,
  handleMessageClick,
  tickets = [],
  startIndex = 0
) => ({
  columns: [
    { name: "sno", align: "center" },
    { name: "Raised_By", align: "left" },
    { name: "subject", align: "left" },
    { name: "description", align: "left" },
    { name: "priority", align: "left" },
    { name: "status", align: "left" },
    { name: "assignedTo", align: "left" },
    { name: "action", align: "center" },
  ],

  rows: tickets.map((ticket, index) => {
    const statusColors = {
      OPEN: "warning",
      CLOSED: "success",
      ON_HOLD: "info",
      TERMINATED: "error",
    };

    const priorityColors = {
      Low: "success",
      Medium: "warning",
      High: "error",
    };

    const userName = ticket.parentId?.fullName || ticket.tutorId?.fullName || "Unknown";

    const userEmail = ticket.parentId?.email || ticket.tutorId?.email || "N/A";

    const userId = ticket.parentId?._id || ticket.tutorId?._id;

    const userType = ticket.parentId ? "PARENT" : "TUTOR";

    const adminName = ticket.assignedTo ? ticket.assignedTo.fullName : "N/A";
    const adminEmail = ticket.assignedTo ? ticket.assignedTo.email : "N/A";

    return {
      sno: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {startIndex + index + 1}
        </ArgonTypography>
      ),
      Raised_By: <RaisedBy name={userName} email={userEmail} userType={userType} userId={userId} />,
      subject: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium" title={ticket.subject}>
          {ticket.subject.split(" ").slice(0, 4).join(" ") +
            (ticket.subject.split(" ").length > 4 ? "..." : "")}
        </ArgonTypography>
      ),
      description: (
        <ArgonTypography
          variant="caption"
          color="text"
          fontWeight="medium"
          title={ticket.description || ""}
        >
          {ticket.description
            ? ticket.description.split(" ").slice(0, 6).join(" ") +
              (ticket.description.split(" ").length > 6 ? "..." : "")
            : "-"}
        </ArgonTypography>
      ),
      priority: (
        <ArgonBadge
          variant="gradient"
          badgeContent={ticket.priority}
          color={priorityColors[ticket.priority] || "secondary"}
          size="xs"
          container
        />
      ),
      status: (
        <ArgonBadge
          variant="gradient"
          badgeContent={ticket.status}
          color={statusColors[ticket.status] || "secondary"}
          size="xs"
          container
        />
      ),
      assignedTo: (
        <ArgonBox display="flex" flexDirection="column">
          <ArgonTypography variant="caption" color="text" fontWeight="medium">
            {adminName}
          </ArgonTypography>
          <ArgonTypography variant="caption" color="secondary">
            {adminEmail}
          </ArgonTypography>
        </ArgonBox>
      ),

      action: (
        <ArgonBox display="flex" alignItems="center" gap={0}>
          {/* View Icon */}
          <Button
            onClick={() => handleViewClick(ticket)}
            variant="text"
            size="large"
            sx={{ minWidth: "12px", p: 0 }}
          >
            <VisibilityIcon color="primary" />
          </Button>

          {/* Edit/Update Status Icon */}
          <Button
            variant="text"
            size="large"
            sx={{ minWidth: "32px", p: 0 }}
            onClick={() => handleStatusClick(ticket)}
          >
            <DriveFileRenameOutlineIcon color="warning" />
          </Button>

          {/* Message Icon */}
          <Button
            variant="text"
            size="large"
            sx={{ minWidth: "32px", p: 0 }}
            onClick={() => handleMessageClick(ticket)}
          >
            <ChatIcon color="success" />
          </Button>
        </ArgonBox>
      ),
    };
  }),
});

export default supportTicketTableData;
